'use client';

import AppImage from '@/components/ui/AppImage';
import useScrollReveal from '@/hooks/useScrollReveal';

export default function BrandonStory() {
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Image Column */}
          <div className="relative h-[400px] sm:h-[500px] md:h-[600px] lg:h-auto overflow-hidden">
            <AppImage
              src="/assets/images/FF053F79-5CFD-48A0-92A9-3170A9DC91FE_1_105_c-1771800084102.jpeg"
              alt="Brandon Green - Founder of The Lone Star Local, wearing blue suit with orange bow tie and colorful shirt"
              width={800}
              height={1000}
              className="w-full h-full object-cover" />
            
            {/* Color accent bar */}
            <div className="absolute top-0 left-0 w-2 h-full bg-brand-teal"></div>
          </div>
          
          {/* Content Column */}
          <div className="bg-brand-offwhite p-6 sm:p-8 md:p-10 lg:p-16 flex flex-col justify-center">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-brand-plum mb-6 sm:mb-8 font-bold border-l-4 border-brand-teal pl-4 sm:pl-6">
              Brandon Green
            </h2>
            
            <div className="space-y-4 sm:space-y-6 font-body text-base sm:text-lg text-text-secondary leading-relaxed pl-4 sm:pl-6">
              <p className="text-lg sm:text-xl text-brand-plum font-bold">
                28 years building brands. 15 of those years working for Fortune 500 companies that didn't need my help. Massive budgets. Layers of account managers. Projects where the marketing spend could have funded an entire nonprofit for a year.
              </p>
              
              <p>
                At Pier 1 Imports, I art directed national campaigns. At Prison Entrepreneurship Program, I served as Director of Marketing Communications for 8 years—telling stories of transformation and leading brand strategy for an organization rebuilding lives. Somewhere in that journey, I started asking myself: who actually needs this work?
              </p>
              
              <div className="my-6 sm:my-8 bg-brand-teal/10 border-l-4 border-brand-teal p-4 sm:p-6">
                <p className="text-brand-plum font-bold italic text-lg sm:text-xl">
                  I kept meeting founders with incredible skills and zero support. Justice-impacted entrepreneurs rebuilding their lives with real talent but no infrastructure. Nonprofits doing transformative work with websites that looked like they were built in 2005.
                </p>
              </div>
              
              <p>
                The gap wasn't talent. It was access. Access to the same strategic thinking, creative direction, and professional execution that Fortune 500 companies take for granted.
              </p>
              
              <p>
                So I left. Started Lone Star Local with a simple premise: second-chance founders and mission-driven organizations deserve the same level of work as any Fortune 500 company. Not charity. Not discounted services. The real thing.
              </p>
              
              <div className="grid grid-cols-2 gap-4 sm:gap-6 my-6 sm:my-8">
                <div className="bg-brand-teal p-6 sm:p-8 text-white text-center">
                  <div className="text-4xl sm:text-5xl font-headline mb-2 font-bold">28</div>
                  <div className="text-xs sm:text-sm font-body uppercase tracking-wider">Years Experience</div>
                </div>
                <div className="bg-brand-orange p-6 sm:p-8 text-white text-center">
                  <div className="text-4xl sm:text-5xl font-headline mb-2 font-bold">100%</div>
                  <div className="text-xs sm:text-sm font-body uppercase tracking-wider">Boutique Focus</div>
                </div>
              </div>
              
              <p>
                We stay small on purpose. Selective clients. Deep partnerships. No assembly line. No junior team members learning on your project. Currently serving as Chief Marketing Operations Officer at Texas Tree Transformations while running Lone Star Local—because I believe in practicing what I preach.
              </p>
              
              <p className="text-lg sm:text-xl text-brand-plum font-bold">
                This isn't about charity. It's about working with people who are building something real and deserve infrastructure that matches their ambition.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}